import { useQueryClient } from "@tanstack/react-query";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "../components/AppShell";
import { GalewayLogo } from "../components/Logo";
import { Alert, Button, Card } from "../components/ui";
import { authClient, followAuthRedirect } from "../lib/auth-client";
import { useMe } from "../lib/session";
import { MfaSetup } from "./auth/MfaSetup";

/** Mandatory MFA enrolment for roles that require it (Owner, Admin). */
export function MfaRequiredPage() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  return (
    <AuthLayout
      title="Secure your account"
      subtitle={
        <>
          As {me.role.name === "Owner" ? "an" : "a"} <strong className="text-fg">{me.role.name}</strong> of {me.company.name}, you must use two-factor
          authentication before continuing.
        </>
      }
    >
      <MfaSetup
        onComplete={async () => {
          await qc.invalidateQueries();
          navigate("/", { replace: true });
        }}
      />
      <button
        type="button"
        className="mt-6 block w-full text-center text-[13px] text-muted hover:text-fg"
        onClick={async () => {
          await authClient.signOut();
          qc.clear();
          navigate("/login", { replace: true });
        }}
      >
        Sign out
      </button>
    </AuthLayout>
  );
}

export function AccessDeniedPage() {
  const [params] = useSearchParams();
  const app = params.get("app");
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-6 text-center">
      <GalewayLogo />
      <Card className="mt-8 w-full max-w-md p-8">
        <span className="mx-auto flex size-12 items-center justify-center rounded-full bg-danger-soft text-danger">
          <ShieldAlert className="size-6" />
        </span>
        <h1 className="mt-4 text-lg font-semibold">You don't have access to this application</h1>
        <p className="mt-2 text-sm text-muted">
          {app && app !== "unknown" ? (
            <>
              Your account isn't permitted to use <strong className="text-fg">{app}</strong>.
            </>
          ) : (
            "Your account isn't permitted to use this application."
          )}{" "}
          If you need it, ask your Galeway administrator to grant access.
        </p>
        <Link to="/" className="mt-6 inline-flex h-10 items-center rounded-lg bg-accent px-4 text-sm font-medium text-accent-fg hover:bg-accent-hover">
          Back to Galeway
        </Link>
      </Card>
    </div>
  );
}

/** OIDC consent (only shown for clients that don't skip consent; Galeway's first-party apps do). */
export function ConsentPage() {
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scopes = (params.get("scope") ?? "").split(" ").filter(Boolean);
  async function decide(accept: boolean) {
    setBusy(true);
    const { data, error: err } = await authClient.$fetch("/oauth2/consent", { method: "POST", body: { accept } });
    setBusy(false);
    if (err) return setError("This request has expired. Return to the application and try again.");
    followAuthRedirect(data);
  }
  return (
    <AuthLayout title="Allow access?" subtitle="An application is requesting access to your Galeway identity.">
      {error && (
        <div className="mb-4">
          <Alert tone="danger">{error}</Alert>
        </div>
      )}
      <ul className="space-y-2 rounded-xl border border-line p-4 text-sm">
        {scopes.map((s) => (
          <li key={s} className="flex items-center gap-2">
            <ShieldCheck className="size-4 text-accent" />
            {{ openid: "Confirm your identity", profile: "Your name", email: "Your email address", offline_access: "Stay signed in" }[s] ?? s}
          </li>
        ))}
      </ul>
      <div className="mt-6 flex gap-2">
        <Button variant="secondary" className="flex-1" onClick={() => decide(false)} disabled={busy}>
          Deny
        </Button>
        <Button className="flex-1" onClick={() => decide(true)} loading={busy}>
          Allow
        </Button>
      </div>
    </AuthLayout>
  );
}

export function NotFoundPage() {
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
      <p className="text-sm font-semibold text-accent">404</p>
      <h1 className="mt-2 text-xl font-semibold">Page not found</h1>
      <p className="mt-2 text-sm text-muted">The page you're looking for doesn't exist or you don't have access to it.</p>
      <Link to="/" className="mt-6 text-sm font-medium text-accent hover:underline">
        Go to dashboard
      </Link>
    </div>
  );
}
